import { useMemo } from "react";
import type { ConversationMessage } from "@/stores/conversation-store";
import { useFloatingFrameStore } from "./floating-frame-store";

export const FLOATING_INITIAL_MESSAGES = "floatingInitialConversationMessages";
const EMPTY: ConversationMessage[] = [];

function isMessage(value: unknown): value is ConversationMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<ConversationMessage>;
  return (
    typeof message.id === "string" &&
    typeof message.content === "string" &&
    typeof message.createdAt === "string" &&
    ["user", "assistant", "system"].includes(message.role ?? "")
  );
}

export function mergeFloatingConversationMessages(
  initial: readonly ConversationMessage[],
  live: readonly ConversationMessage[],
): readonly ConversationMessage[] {
  if (!initial.length) return live;
  const liveIds = new Set(live.map((message) => message.id));
  const retained = initial.filter((message) => {
    if (liveIds.has(message.id)) return false;
    if (!message.id.startsWith("cursor-reply-")) return true;
    return !live.some((candidate) => {
      if (
        candidate.role !== "assistant" ||
        !candidate.content ||
        candidate.id.startsWith("cursor-reply-")
      )
        return false;
      if (candidate.content === message.content) return true;
      const sameExecution =
        message.executionId && candidate.executionId === message.executionId;
      if (!sameExecution) return false;
      return (
        (!candidate.activityKind && !candidate.isStreaming) ||
        candidate.content.startsWith(message.content) ||
        message.content.startsWith(candidate.content)
      );
    });
  });
  return [...retained, ...live].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  );
}

/** Initial cursor-only output survives transfer until canonical output arrives. */
export function useFloatingConversationMessages(
  frameId: string,
  conversationId: string | undefined,
  live: readonly ConversationMessage[],
) {
  const frame = useFloatingFrameStore((state) => state.frames[frameId]?.frame);
  return useMemo(() => {
    const originalConversation =
      frame?.metadata.conversationId ?? frame?.metadata.conversation_id;
    if (originalConversation !== conversationId) return live;
    const initial = frame?.content[FLOATING_INITIAL_MESSAGES];
    return mergeFloatingConversationMessages(
      Array.isArray(initial) ? initial.filter(isMessage) : EMPTY,
      live,
    );
  }, [conversationId, frame, live]);
}
